// Copyright (c) StrangeBee 2023

import { useContext } from 'react';
import { FilterInput, CaseCustomField, SeverityValues, ObjectTypeV1 as ObjectType } from 'thehive-sdk';
import { ListContext } from '@Context/ListProvider';
import { useListAllFields } from '@Queries/describeQueries';
import moment from 'moment';

interface UseQuickFiltersInterface {
    fieldName: ObjectType;
    onChange?: (newQuickFilter: QuickFilterInput, concatenate?: boolean) => void;
}

export interface QuickFilterInput {
    field: FilterInput['field'];
    operator?: FilterInput['operator'];
    values?: FilterInput['values'];
}

export interface QuickFiltersAvailable {
    tags: (values: string[], concatenate?: boolean) => void;
    date: (date: number, field?: string) => void;
    severity: (value: SeverityValues) => void;
    status: (value: string, field?: string) => void;
    assignee: (value?: string | null) => void;
    dataType: (value: string) => void;
    bool: (field: string, value?: string) => void;
    flag: (value?: boolean) => void;
    ioc: (value?: boolean) => void;
    sighted: (value?: boolean) => void;
    customField: (customField: CaseCustomField) => void;
    custom: (filter: QuickFilterInput) => void;
}

const useQuickFilters = ({
    fieldName = ObjectType.CASE,
    onChange,
}: UseQuickFiltersInterface): QuickFiltersAvailable => {
    const { data: allFieldsData } = useListAllFields();

    const describeField = allFieldsData?.[fieldName];

    const { setFilters, setTempFilters, setEditFilters, setPage } = useContext(ListContext);

    const addQuickFilter = ({ field = '', operator = '', values }: QuickFilterInput, concatenate = false): void => {
        if (!describeField) {
            return;
        }

        const attribute = describeField.attributes.find(({ name }) => name === field);

        if (!attribute) {
            return;
        }

        const newFilter: FilterInput = {
            field,
            operator,
            values,
            path: fieldName || '',
            describeAttribute: attribute,
            type: describeField?.attributes.find((attribute) => attribute.name === field)?.type || 'string',
        };

        onChange?.(newFilter, concatenate);

        setPage(0);
        setEditFilters(true);
        setFilters((filters) => [...filters.filter(({ field: filtersField }) => filtersField !== field), newFilter]);
        setTempFilters((tempFilters) => [
            ...tempFilters.filter(({ field: tempFiltersField }) => tempFiltersField !== field),
            newFilter,
        ]);
    };

    const manager = {
        tags: (values: string[], eraseFilters?: boolean): void =>
            addQuickFilter({ operator: 'any', field: 'tags', values }, eraseFilters),
        date: (date: number, field = 'startDate'): void =>
            addQuickFilter({
                field,
                operator: 'custom',
                values: [
                    moment(date).hours(0).minutes(0).seconds(0).milliseconds(0).valueOf(),
                    moment(date).hours(23).minutes(59).seconds(59).milliseconds(999).valueOf(),
                ],
            }),
        severity: (value: SeverityValues): void =>
            addQuickFilter({ operator: 'any', field: 'severity', values: [value] }),
        status: (value: string, field = 'status'): void => addQuickFilter({ operator: 'any', field, values: [value] }),
        assignee: (value?: string | null): void =>
            addQuickFilter({
                operator: value ? 'any' : 'is empty',
                field: 'assignee',
                values: value ? [value] : undefined,
            }),
        dataType: (value: string): void => addQuickFilter({ operator: 'any', field: 'dataType', values: [value] }),
        bool: (field: string, value?: string): void => addQuickFilter({ operator: value || 'true', field }),
        flag: (value?: boolean): void => manager.bool('flag', value + ''),
        ioc: (value?: boolean): void => manager.bool('ioc', value + ''),
        sighted: (value?: boolean): void => manager.bool('sighted', value + ''),
        customField: ({ name, type, value }: CaseCustomField): void => {
            switch (type) {
                case 'boolean':
                    manager.bool(`customFields.${name}`);
                    break;
                case 'date':
                    manager.date(value as number, `customFields.${name}`);
                    break;
                default:
                    addQuickFilter({
                        operator: ['integer', 'float'].includes(type) ? 'eq' : 'any',
                        field: `customFields.${name}`,
                        values: [value],
                    });
                    break;
            }
        },
        custom: (filter: QuickFilterInput): void => addQuickFilter(filter),
    };

    return manager;
};

export default useQuickFilters;
